// This file exports a hook alongside JSX helper functions. Fast Refresh doesn't
// apply to hook files, so the component-export mixing rule is suppressed here.
/* eslint-disable react-refresh/only-export-components */
import { plural, t, Trans } from "@lingui/macro";
import {
  useConfigContext,
  useDrawerContext,
  useGuardedUpdater,
  useSubscriptionContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { useCallback, useMemo, useRef, type ReactNode } from "react";
import { add, isValid, parseISO } from "date-fns";
import { Box, Stack, Typography } from "@mui/material";
import {
  Plan,
  RedesignrChip,
  WfButton,
  WfIconContentWarningRegular,
  WfMenuList,
  WfMenuListItem,
  WfMessage,
} from "@WahooFitness/redesignr";
import type { I18n } from "@lingui/core";
import AppStoreLogoSvg from "./AppStoreLogoSvg";
import GooglePlayLogoSvg from "./GooglePlayLogoSvg";
import { useLingui } from "@lingui/react";
import useRevenueCat from "./useRevenueCat";
import formatDate from "@/services/formatDate";
import useAuthTokenClient from "./useAuthTokenClient";
import { openExternalLink } from "./openExternalLink";
import { ReactRouterLinkWrapper } from "../MenuList/ReactRouterLinkWrapper";
import { Tag } from "@carbon/icons-react";
import { ClickTarget, EventType, SourcePage } from "@/services/Analytics/EventTypes";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import UISubscriptionStatusEnum from "./UISubscriptionStatusEnum";
import getUISubscriptionState from "./getUISubscriptionState";
import { getPendingSubscriptionChange } from "./getPendingSubscriptionChange";
import { SubscriptionTier, SubscriptionType } from "@WahooFitness/cloud-client-types";
import { capitalize, formatPromoPeriod, getInterval } from "./displayUtils";
import { isSystmSubscription, getTierForPlanCode } from "./planCodes";
import { buildChangePlanUrl } from "./accountsPortalUrl";
import { buildCancelConfirmationDrawer } from "./cancelConfirmationDrawer";
import {
  buildAppleManagementDrawer,
  buildGoogleManagementDrawer,
  buildSystmManagementDrawer,
  buildWahooManagementDrawer,
  APPLE_MANAGE_URL,
  GOOGLE_MANAGE_URL,
} from "./storeManagementDrawers";

// ─── Constants ────────────────────────────────────────────────────────────────

export const PROCESSOR = {
  APPLE: "apple",
  GOOGLE: "google",
  WAHOO: "wahoo",
  STRIPE: "stripe",
} as const;

const SUBSCRIPTION_INTERVAL_MAP = {
  daily: "days",
  weekly: "weeks",
  monthly: "months",
  yearly: "years",
} as const;
type SubscriptionIntervalKey = keyof typeof SUBSCRIPTION_INTERVAL_MAP;

// ─── Types ────────────────────────────────────────────────────────────────────

type SubscriptionDisplayItem = { label: string; value: string | ReactNode };

export type SubscriptionDisplayData = {
  tag: ReactNode | null;
  items: SubscriptionDisplayItem[];
  description?: ReactNode | null;
  action: ReactNode | null;
  tier?: Plan;
  canceled?: boolean;
  trialing?: boolean;
  footerNote?: ReactNode;
};

// ─── Active states ────────────────────────────────────────────────────────────

const activeSubscriptionStates = [
  UISubscriptionStatusEnum.ActiveSubscription,
  UISubscriptionStatusEnum.ActiveCancelledSubscription,
  UISubscriptionStatusEnum.OnTrialNoSubscription,
  UISubscriptionStatusEnum.OnTrialFutureSubscription,
  UISubscriptionStatusEnum.PaymentError,
  UISubscriptionStatusEnum.HonorarySubscription,
  UISubscriptionStatusEnum.HonoraryRecurringSubscription,
  UISubscriptionStatusEnum.PriceCoupon,
  UISubscriptionStatusEnum.ImmediateIAPSubscription,
  UISubscriptionStatusEnum.OnIapTrial,
  UISubscriptionStatusEnum.ActiveTimeOffPromotion,
  UISubscriptionStatusEnum.TrialingTimeOffPromotion,
  UISubscriptionStatusEnum.ActiveSubscriptionWithPendingDowngrade,
];

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function formatInterval(interval: string | undefined): string {
  switch (interval) {
    case "monthly":
      return t`/month`;
    case "yearly":
      return t`/year`;
    default:
      return "";
  }
}

function calculatePromoPeriodEndDate(
  interval?: SubscriptionIntervalKey,
  intervalCount?: number,
  startDate?: string
): string | null {
  if (!interval || !intervalCount || !startDate) return null;
  const promoEnd = add(new Date(startDate), {
    [SUBSCRIPTION_INTERVAL_MAP[interval]]: intervalCount,
  });
  // isValid guards a malformed startDate so toISOString never throws.
  return isValid(promoEnd) ? promoEnd.toISOString() : null;
}

// omit date row if the date is invalid or null/undefined
function dateItem(label: string, date: string | null | undefined): SubscriptionDisplayItem | null {
  if (!date || !isValid(parseISO(date))) return null;
  return { label, value: formatDate({ date, format: "PP" }) };
}

function isDisplayItem(item: SubscriptionDisplayItem | null): item is SubscriptionDisplayItem {
  return item !== null;
}

export function isAppStoreMismatch(
  fitnessAppId: number,
  processorName: string | undefined
): boolean {
  return (
    (fitnessAppId === 30 && processorName === PROCESSOR.GOOGLE) ||
    (fitnessAppId === 31 && processorName === PROCESSOR.APPLE)
  );
}

function isMobileProcessor(processorName: string | undefined): boolean {
  return processorName === PROCESSOR.APPLE || processorName === PROCESSOR.GOOGLE;
}

export function isWahooApp(fitnessAppId: number): boolean {
  return fitnessAppId === 30 || fitnessAppId === 31;
}

function buildDiscountTagLabel(sub: SubscriptionType, noCoupon = false): string | null {
  if (noCoupon) {
    const { intervalCount: formattedIntervalCount, interval: formattedInterval } = getInterval(
      sub.interval_count,
      sub.interval,
      sub.current_period_start,
      sub.current_period_end
    );
    const durationPluralized = plural(formattedIntervalCount, {
      one: `First # ${formattedInterval} free`,
      other: `First # ${formattedInterval}s free`,
    });
    return t`${durationPluralized}`;
  }

  const {
    interval: couponInterval,
    interval_count: couponIntervalCount,
    pct_off,
  } = sub.subscription_coupon_redemption?.subscription_coupon_promotion ?? {};

  if (!couponInterval || !couponIntervalCount) return null;

  const duration = couponDurationPhrase(couponInterval, couponIntervalCount);

  if (pct_off !== null) return t`${pct_off}% off first ${duration}`;
  return t`First ${duration} free`;
}

// "$X off first month" for fixed-dollar coupons (pct_off === null, so
// buildDiscountTagLabel would mislabel them as free time), else buildDiscountTagLabel.
function couponChipLabel(sub: SubscriptionType, i18n: I18n): string | null {
  const couponPromotion = sub.subscription_coupon_redemption?.subscription_coupon_promotion;
  if (couponPromotion?.coupon_type === "discounted_amount" && couponPromotion.interval_count) {
    const currency = sub.currency ?? sub.subscription_currency?.currency ?? "USD";
    const fullPriceCents = sub.subscription_currency?.amount_cents;
    const discountOffCents =
      fullPriceCents != null && sub.subscription_amount_cents != null
        ? fullPriceCents - sub.subscription_amount_cents
        : undefined;
    if (discountOffCents != null) {
      return t`${i18n.number(discountOffCents / 100, {
        style: "currency",
        currency,
      })} off first ${couponDurationPhrase(couponPromotion.interval, couponPromotion.interval_count)}`;
    }
  }
  return buildDiscountTagLabel(sub);
}

function couponDurationLabel(interval: string | undefined): string {
  switch (interval) {
    case "daily":
      return t`day`;
    case "weekly":
      return t`week`;
    case "monthly":
      return t`month`;
    case "yearly":
      return t`year`;
    default:
      return interval ?? "";
  }
}

// "month" for a single interval, "3 months" for multi-interval coupons. Each unit is
// its own full ICU plural message so every locale controls the plural form.
function couponDurationPhrase(
  interval: string | undefined,
  intervalCount: number | undefined
): string {
  const count = intervalCount ?? 1;
  if (count <= 1) return couponDurationLabel(interval);
  switch (interval) {
    case "daily":
      return plural(count, { one: "# day", other: "# days" });
    case "weekly":
      return plural(count, { one: "# week", other: "# weeks" });
    case "monthly":
      return plural(count, { one: "# month", other: "# months" });
    case "yearly":
      return plural(count, { one: "# year", other: "# years" });
    default:
      return `${count} ${interval ?? ""}`;
  }
}

function appStoreMismatchDescription(processorName: string | undefined): ReactNode {
  return (
    <Stack alignItems="center">
      <Typography variant="prose-base-medium" color="text.primary">
        <Trans>To manage subscription go to</Trans>
      </Typography>
      {processorName === PROCESSOR.APPLE ? <AppStoreLogoSvg /> : <GooglePlayLogoSvg />}
    </Stack>
  );
}

// ─── Reusable menu item fragments ─────────────────────────────────────────────

function TrackedChangePlanLink(props: Parameters<typeof ReactRouterLinkWrapper>[0]) {
  const { logEvent } = useLogEvent();
  return (
    <ReactRouterLinkWrapper
      {...props}
      onClick={(e) => {
        logEvent("subscription_change_plan", {
          event_type: EventType.click,
          click_target: ClickTarget.change_plan,
          source_page_name: SourcePage.my_subscription,
        });
        props.onClick?.(e);
      }}
    />
  );
}

function changePlanMenuItem(onChangePlanPrompt?: () => void): ReactNode {
  if (onChangePlanPrompt) {
    return (
      <WfMenuListItem
        id="change-plan"
        content={t`Change plan`}
        variant="action"
        action={onChangePlanPrompt}
      />
    );
  }
  return (
    <WfMenuListItem
      id="change-plan"
      content={t`Change plan`}
      variant="internalLink"
      href="/change-plan"
      linkComponent={TrackedChangePlanLink}
    />
  );
}

function subscriptionManagementItems(
  onButtonClick: (target: ClickTarget) => void,
  onChangePlanPrompt?: () => void,
  isCancelled = false
): ReactNode {
  return (
    <>
      <WfMenuList disableGutters>{changePlanMenuItem(onChangePlanPrompt)}</WfMenuList>
      <WfMenuList disableGutters>
        {isCancelled ? (
          <WfMenuListItem
            id="resume-subscription"
            content={t`Resume subscription`}
            variant="action"
            action={() => onButtonClick(ClickTarget.resubscribe)}
          />
        ) : (
          <WfMenuListItem
            id="cancel-subscription"
            content={t`Cancel subscription`}
            variant="action"
            isDestructive
            action={() => onButtonClick(ClickTarget.cancel_subscription)}
          />
        )}
      </WfMenuList>
    </>
  );
}

function changePlanOnlyButton(onChangePlanPrompt?: () => void): ReactNode {
  return <WfMenuList disableGutters>{changePlanMenuItem(onChangePlanPrompt)}</WfMenuList>;
}

function subscribeToProDescription(onSubscribeToPro: () => void): ReactNode {
  return (
    <Stack gap={1.5}>
      <Typography variant="prose-base" color="text.primary">
        <Trans>Keep your pro features after your free trial ends.</Trans>
      </Typography>
      <WfButton hierarchy="primary" fullWidth onClick={onSubscribeToPro} size="large">
        <Trans>Subscribe to Pro</Trans>
      </WfButton>
    </Stack>
  );
}

// ─── Per-state builder context ────────────────────────────────────────────────

interface BuildContext {
  sub: SubscriptionType;
  formattedAmount: string;
  i18n: I18n;
  fitnessAppId: number;
  onButtonClick: (target: ClickTarget) => void;
  onChangePlanPrompt?: () => void;
  onSubscribeToPro: () => void;
  activeTier: Plan;
  purchaseInProgress: boolean;
  pendingDowngradeDate?: string;
  pendingDowngradePriceString?: string;
  // Store-offering price of an IAP pending change; undefined for card/web changes or when
  // offerings aren't loaded.
  pendingIapPriceString?: string;
}

// ─── Per-state builders ───────────────────────────────────────────────────────

function buildImmediateIAPData({
  sub,
  onButtonClick,
  onChangePlanPrompt,
  activeTier,
  fitnessAppId,
}: BuildContext): SubscriptionDisplayData {
  const mismatch = isAppStoreMismatch(fitnessAppId, sub.subscription_processor?.name);
  return {
    tier: activeTier,
    tag:
      sub.status === "trialing" ? (
        <RedesignrChip label={t`Free trial`} size="small" color="info" readOnly />
      ) : (
        <RedesignrChip label={t`Active`} size="small" color="info" readOnly />
      ),
    items: [],
    description: mismatch ? appStoreMismatchDescription(sub.subscription_processor?.name) : null,
    action: subscriptionManagementItems(onButtonClick, onChangePlanPrompt),
  };
}

function buildPriceCouponData({
  sub,
  formattedAmount,
  i18n,
  onButtonClick,
  onChangePlanPrompt,
  activeTier,
}: BuildContext): SubscriptionDisplayData {
  const endDate = sub.subscription_coupon_redemption
    ? calculatePromoPeriodEndDate(
        sub.subscription_coupon_redemption.subscription_coupon_promotion
          ?.interval as SubscriptionIntervalKey,
        sub.subscription_coupon_redemption.subscription_coupon_promotion?.interval_count,
        sub.subscription_coupon_redemption.redeemed_at
      )
    : null;
  const subscriptionAmount = sub.subscription_pending_change?.total_amount_cents
    ? sub.subscription_pending_change.total_amount_cents / 100
    : 0;
  const couponPromotion = sub.subscription_coupon_redemption?.subscription_coupon_promotion;
  const isTrialing = sub.status === "trialing";
  // A fixed-dollar ("discounted_amount") coupon also has pct_off === null, so it must be
  // detected by coupon_type and excluded from the "time free" branch — it is a reduced
  // price, not free time.
  const isDiscountedAmount = !isTrialing && couponPromotion?.coupon_type === "discounted_amount";
  const isTimeFree = !isTrialing && !isDiscountedAmount && couponPromotion?.pct_off === null;

  // discounted_amount specifics: standard (struck) price and whether the subscription
  // has billing info (a scheduled renewal) which drives the two design variants.
  const currency = sub.currency ?? sub.subscription_currency?.currency ?? "USD";
  const fullPriceCents = sub.subscription_currency?.amount_cents;
  const hasBillingInfo = !!sub.next_renewal_at;

  const chipLabel = isTrialing
    ? t`Trial`
    : isDiscountedAmount
      ? couponChipLabel(sub, i18n)
      : buildDiscountTagLabel(sub);
  const chipColor = isTrialing ? "default" : "info";
  // TODO: TAJ-721 replace with WfIconMiscTag icon once available in redesignr
  const chipIcon = isTrialing ? undefined : <Tag />;

  const discountedAmountItems: SubscriptionDisplayItem[] = [
    {
      label: t`Current price`,
      value: (
        <Typography variant="prose-base" color="text.primary">
          {fullPriceCents != null && (
            <>
              <s>{t`${i18n.number(fullPriceCents / 100, { style: "currency", currency })}`}</s>{" "}
            </>
          )}
          {t`${formattedAmount}`}
        </Typography>
      ),
    },
    dateItem(t`Discount ends`, endDate),
    dateItem(t`Renews on`, sub.next_renewal_at),
  ].filter(isDisplayItem);

  const items: SubscriptionDisplayItem[] = isTrialing
    ? [
        dateItem(t`Trial started`, sub.trial_start),
        dateItem(t`Trial ends`, sub.trial_end),
        {
          label: t`Promo period`,
          value: formatPromoPeriod(sub.subscription_coupon_redemption?.redeemed_at, endDate),
        },
      ].filter(isDisplayItem)
    : isDiscountedAmount
      ? discountedAmountItems
      : isTimeFree
        ? [
            {
              label: t`Promo period`,
              value: formatPromoPeriod(sub.subscription_coupon_redemption?.redeemed_at, endDate),
            },
            { label: t`Then`, value: t`${formattedAmount}` },
          ]
        : [
            {
              label: t`Current Price`,
              value: (
                <Typography variant="prose-base" color="text.primary">
                  <s>
                    {t`${i18n.number(subscriptionAmount, {
                      style: "currency",
                      currency: sub.subscription_pending_change?.currency ?? "USD",
                    })}`}
                  </s>{" "}
                  {t`${formattedAmount}`}
                </Typography>
              ),
            },
            dateItem(t`Full price starts`, sub.subscription_pending_change?.current_period_start),
            dateItem(t`Renews on`, sub.next_renewal_at),
          ].filter(isDisplayItem);

  return {
    tier: activeTier,
    tag: (
      <RedesignrChip label={chipLabel} size="small" color={chipColor} icon={chipIcon} readOnly />
    ),
    items,
    action: subscriptionManagementItems(onButtonClick, onChangePlanPrompt),
    description:
      isTrialing || (isDiscountedAmount && !hasBillingInfo) ? (
        <Typography variant="prose-base" color="text.primary">
          <Trans>
            Add payment information to continue your subscription after the promotional period ends.
          </Trans>
        </Typography>
      ) : null,
  };
}

function buildHonorarySubscriptionData({
  sub,
  formattedAmount,
  i18n,
  onButtonClick,
  onChangePlanPrompt,
  activeTier,
}: BuildContext): SubscriptionDisplayData {
  const subscriptionAmount = sub.subscription_pending_change?.total_amount_cents
    ? sub.subscription_pending_change.total_amount_cents / 100
    : 0;
  const isTrialing = sub.status === "trialing";
  const chipLabel = isTrialing ? t`Trial` : t`Complimentary`;
  const chipColor = isTrialing ? "default" : "info";

  const items: SubscriptionDisplayItem[] = isTrialing
    ? [
        dateItem(t`Trial started`, sub.trial_start),
        {
          label: t`Billing`,
          value: (
            <Typography variant="prose-base" color="text.primary">
              <s>
                {t`${i18n.number(subscriptionAmount, {
                  style: "currency",
                  currency: sub.subscription_pending_change?.currency ?? "USD",
                })}`}
              </s>{" "}
              {t`${formattedAmount}`}
            </Typography>
          ),
        },
        dateItem(t`Billing starts`, sub.subscription_pending_change?.current_period_start),
      ].filter(isDisplayItem)
    : [
        {
          label: t`Promo period`,
          value: formatPromoPeriod(sub.current_period_start, sub.current_period_end),
        },
      ];

  return {
    tier: activeTier,
    tag: <RedesignrChip label={chipLabel} size="small" color={chipColor} readOnly />,
    items,
    action: isTrialing ? subscriptionManagementItems(onButtonClick, onChangePlanPrompt) : null,
    description: !isTrialing ? (
      <Typography variant="prose-base" color="text.primary">
        <Trans>
          Add payment information to continue your subscription after the promotional period ends.
        </Trans>
      </Typography>
    ) : null,
  };
}

function buildOnTrialNoSubscriptionData({
  sub,
  onChangePlanPrompt,
  onSubscribeToPro,
  activeTier,
}: BuildContext): SubscriptionDisplayData {
  return {
    tier: activeTier,
    trialing: true,
    tag: <RedesignrChip label={t`Free trial`} size="small" color="info" readOnly />,
    items: [
      dateItem(t`Trial started`, sub.trial_start),
      dateItem(t`Trial ends`, sub.trial_end),
    ].filter(isDisplayItem),
    footerNote:
      activeTier === "core" ? (
        <Box
          width="100%"
          sx={{
            display: "flex",
            pl: 1,
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 0.5,
            borderLeft: "2px solid",
            borderColor: "info.main",
          }}
        >
          <Typography variant="prose-xs-medium" color="text.secondary">
            <Trans>Free trial includes full Pro access for 14 days</Trans>
          </Typography>
        </Box>
      ) : undefined,
    description: subscribeToProDescription(onSubscribeToPro),
    action: changePlanOnlyButton(onChangePlanPrompt),
  };
}

function buildPaymentErrorData({
  sub,
  formattedAmount,
  onButtonClick,
  onChangePlanPrompt,
  activeTier,
  fitnessAppId,
}: BuildContext): SubscriptionDisplayData {
  const tierLabel = activeTier.toUpperCase();
  const mismatch = isAppStoreMismatch(fitnessAppId, sub.subscription_processor?.name);
  return {
    tier: activeTier,
    tag: (
      <RedesignrChip
        label={t`Payment failed`}
        size="small"
        color="warning"
        readOnly
        startIcon={<WfIconContentWarningRegular />}
      />
    ),
    items: [
      { label: t`Billing`, value: t`${formattedAmount}` },
      dateItem(t`Renews on`, sub.next_renewal_at),
    ].filter(isDisplayItem),
    description: (
      <Stack gap={1.5}>
        {mismatch && appStoreMismatchDescription(sub.subscription_processor?.name)}
        <WfMessage
          hideTitleIcon
          title={t`There was an issue with your last payment`}
          hierarchy="warning"
          description={t`Please review your billing information to avoid interruption to your ${tierLabel} access.`}
        />

        <WfButton
          hierarchy="tertiary"
          onClick={() => onButtonClick(ClickTarget.manage_subscription)}
          size="large"
        >
          <Trans>Update payment</Trans>
        </WfButton>
      </Stack>
    ),
    action: subscriptionManagementItems(onButtonClick, onChangePlanPrompt),
  };
}

// resubscribe to current plan if cancelled, so only show change plan option if not on base plan and cancelled
function buildActiveCancelledSubscriptionData({
  sub,
  onButtonClick,
  onChangePlanPrompt,
  activeTier,
  purchaseInProgress,
}: BuildContext): SubscriptionDisplayData {
  return {
    tier: activeTier,
    canceled: true,
    tag: <RedesignrChip label={t`Canceled`} size="small" color="default" readOnly />,
    items: [
      dateItem(t`Access until`, sub.cancel_at),
      { label: t`No further charges.`, value: "" },
    ].filter(isDisplayItem),
    description: (
      <WfButton
        hierarchy="primary"
        fullWidth
        disabled={purchaseInProgress}
        loading={purchaseInProgress}
        onClick={() => onButtonClick(ClickTarget.resubscribe)}
        size="large"
      >
        <Trans>Resubscribe</Trans>
      </WfButton>
    ),
    action: subscriptionManagementItems(onButtonClick, onChangePlanPrompt, true),
  };
}

function buildOnTrialFutureSubscriptionData({
  sub,
  formattedAmount,
  i18n,
  fitnessAppId,
  onButtonClick,
  onChangePlanPrompt,
  activeTier,
  pendingIapPriceString,
}: BuildContext): SubscriptionDisplayData {
  const pendingChange = getPendingSubscriptionChange(sub);
  const mismatch = isAppStoreMismatch(fitnessAppId, sub.subscription_processor?.name);

  // Coupon redeemed during the trial: the subscription carries the discounted amount,
  // and the pending change is the full-price phase of the billing schedule.
  const currency = sub.currency ?? sub.subscription_currency?.currency ?? "USD";
  const fullPriceCents = sub.subscription_currency?.amount_cents;
  const hasCouponDiscount =
    pendingChange?.source !== "iap" &&
    !!sub.subscription_coupon_redemption &&
    fullPriceCents != null &&
    sub.subscription_amount_cents != null &&
    sub.subscription_amount_cents < fullPriceCents;

  // IAP pending changes have no cloud amount: show the store price (all offerings are
  // monthly), or omit the row rather than render $0.00.
  const billingItem: SubscriptionDisplayItem | null =
    pendingChange?.source === "iap"
      ? pendingIapPriceString
        ? { label: t`Billing`, value: `${pendingIapPriceString}${formatInterval("monthly")}` }
        : null
      : hasCouponDiscount
        ? {
            label: t`Billing`,
            value: (
              <Typography variant="prose-base" color="text.primary">
                <s>{t`${i18n.number(fullPriceCents / 100, { style: "currency", currency })}`}</s>{" "}
                {t`${formattedAmount}`}
              </Typography>
            ),
          }
        : {
            label: t`Billing`,
            value: t`${i18n.number((pendingChange?.subscription_amount_cents ?? 0) / 100, {
              style: "currency",
              currency: pendingChange?.currency ?? "USD",
            })}${formatInterval(pendingChange?.interval)}`,
          };

  const items: SubscriptionDisplayItem[] = hasCouponDiscount
    ? [
        dateItem(t`Trial started`, sub.trial_start),
        billingItem,
        dateItem(t`Billing starts`, sub.trial_end ?? sub.next_renewal_at),
        dateItem(t`Full price starts`, pendingChange?.current_period_start),
      ].filter(isDisplayItem)
    : [
        dateItem(t`Trial started`, sub.trial_start),
        billingItem,
        dateItem(t`Billing starts`, pendingChange?.current_period_start),
      ].filter(isDisplayItem);

  const promoChipLabel = hasCouponDiscount ? couponChipLabel(sub, i18n) : null;
  const trialChip = <RedesignrChip label={t`Free trial`} size="small" color="info" readOnly />;

  return {
    tier: activeTier,
    tag: promoChipLabel ? (
      <Stack direction="row" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
        {trialChip}
        <RedesignrChip label={promoChipLabel} size="small" color="info" icon={<Tag />} readOnly />
      </Stack>
    ) : (
      trialChip
    ),
    items,
    description: mismatch ? appStoreMismatchDescription(sub.subscription_processor?.name) : null,
    action: subscriptionManagementItems(onButtonClick, onChangePlanPrompt),
  };
}

function buildOnIapTrialData({
  sub,
  fitnessAppId,
  onButtonClick,
  onChangePlanPrompt,
  activeTier,
}: BuildContext): SubscriptionDisplayData {
  const mismatch = isAppStoreMismatch(fitnessAppId, sub.subscription_processor?.name);
  return {
    tier: activeTier,
    tag: <RedesignrChip label={t`Free trial`} size="small" color="info" readOnly />,
    items: [
      dateItem(t`Trial started`, sub.trial_start),
      dateItem(t`Billing starts`, sub.next_renewal_at),
    ].filter(isDisplayItem),
    description: mismatch ? appStoreMismatchDescription(sub.subscription_processor?.name) : null,
    action: subscriptionManagementItems(onButtonClick, onChangePlanPrompt),
  };
}

function buildActiveSubscriptionData({
  sub,
  formattedAmount,
  fitnessAppId,
  onButtonClick,
  onChangePlanPrompt,
  activeTier,
}: BuildContext): SubscriptionDisplayData {
  const mismatch = isAppStoreMismatch(fitnessAppId, sub.subscription_processor?.name);
  return {
    tier: activeTier,
    tag: <RedesignrChip label={t`Active`} size="small" color="info" readOnly />,
    items: [
      { label: t`Billing`, value: t`${formattedAmount}` },
      dateItem(t`Renews on`, sub.next_renewal_at),
    ].filter(isDisplayItem),
    description: mismatch ? appStoreMismatchDescription(sub.subscription_processor?.name) : null,
    action: subscriptionManagementItems(onButtonClick, onChangePlanPrompt),
  };
}

function buildActiveSubscriptionWithPendingDowngradeData({
  sub,
  fitnessAppId,
  onButtonClick,
  onChangePlanPrompt,
  activeTier,
  pendingDowngradeDate,
  pendingDowngradePriceString,
}: BuildContext): SubscriptionDisplayData {
  const mismatch = isAppStoreMismatch(fitnessAppId, sub.subscription_processor?.name);
  const items: SubscriptionDisplayData["items"] = [
    dateItem(t`Access until`, pendingDowngradeDate ?? sub.next_renewal_at),
  ].filter(isDisplayItem);
  if (pendingDowngradePriceString) {
    items.push({
      label: t`Downgrades to`,
      value: t`Core (${pendingDowngradePriceString}/month)`,
    });
  }
  return {
    tier: activeTier,
    tag: <RedesignrChip label={t`Downgrading`} size="small" color="info" readOnly />,
    items,
    description: mismatch ? appStoreMismatchDescription(sub.subscription_processor?.name) : null,
    action: subscriptionManagementItems(onButtonClick, onChangePlanPrompt),
  };
}

function buildHonoraryRecurringSubscriptionData({
  activeTier,
}: BuildContext): SubscriptionDisplayData {
  return {
    tier: activeTier,
    tag: <RedesignrChip label={t`Complimentary`} size="small" color="info" readOnly />,
    items: [{ label: t`No payment required`, value: "" }],
    action: null,
    description: null,
  };
}

function buildActiveTimeOffPromotionData({
  sub,
  onButtonClick,
  onChangePlanPrompt,
  activeTier,
}: BuildContext): SubscriptionDisplayData {
  const tagLabel = buildDiscountTagLabel(sub, true);
  return {
    tier: activeTier,
    tag: tagLabel ? (
      // TODO: TAJ-721 replace with WfIconMiscTag icon once available in redesignr
      <RedesignrChip label={tagLabel} size="small" color="info" icon={<Tag />} readOnly />
    ) : null,
    items: [
      {
        label: t`Promo period`,
        value: formatPromoPeriod(sub.current_period_start, sub.current_period_end),
      },
    ],
    action: subscriptionManagementItems(onButtonClick, onChangePlanPrompt),
    description: (
      <Typography variant="prose-base" color="text.primary">
        <Trans>
          Add payment information to continue your subscription after the promotional period ends.
        </Trans>
      </Typography>
    ),
  };
}

function buildTrialingTimeOffPromotionData({
  sub,
  onChangePlanPrompt,
  activeTier,
}: BuildContext): SubscriptionDisplayData {
  return {
    tier: activeTier,
    tag: <RedesignrChip label={t`Free trial`} size="small" color="info" readOnly />,
    items: [
      dateItem(t`Trial started`, sub.trial_start),
      dateItem(t`Trial ends`, sub.trial_end),
      {
        label: t`Promo period`,
        value: formatPromoPeriod(sub.current_period_start, sub.current_period_end),
      },
    ].filter(isDisplayItem),
    description: (
      <Typography variant="prose-base" color="text.primary">
        <Trans>
          Add payment information to continue your subscription after the promotional period ends.
        </Trans>
      </Typography>
    ),
    action: changePlanOnlyButton(onChangePlanPrompt),
  };
}

function buildBaseSubscriptionData({
  formattedAmount,
  onChangePlanPrompt,
}: BuildContext): SubscriptionDisplayData {
  return {
    tier: "base",
    tag: <RedesignrChip label={t`Included`} size="small" color="info" readOnly />,
    items: [
      {
        label: t`Billing`,
        value: formattedAmount,
      },
    ],
    action: changePlanOnlyButton(onChangePlanPrompt),
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

function useTieredMembership(referrer?: string) {
  const { userSubscription, subscriptionError, subscriptionIsLoading } = useSubscriptionContext();
  const {
    fitnessAppId,
    cloudConfig: { wahooMyAccountUrl },
  } = useConfigContext();
  const { i18n } = useLingui();
  const { setDrawer, handleClose } = useDrawerContext();
  const { getAuthTokens } = useAuthTokenClient();
  const { logEvent } = useLogEvent();
  const { update: guardedCancelUpdate } = useGuardedUpdater();

  // Cancel-confirmation's onCancelPlan can run long after the drawer opened (network delay,
  // user hesitation), so it reads the processor fresh from this ref instead of closing over a
  // snapshot taken when the drawer was first shown.
  const userSubscriptionRef = useRef(userSubscription);
  userSubscriptionRef.current = userSubscription;

  const {
    proPkg,
    corePkg,
    offeringsLoading,
    offeringsError,
    makePurchase,
    purchaseInProgress,
    convertPeriodToDays,
    customerInfo,
  } = useRevenueCat();

  const logSubscriptionEvent = useCallback(
    (eventName: string, clickTarget: ClickTarget) => {
      logEvent(eventName, {
        event_type: EventType.click,
        click_target: clickTarget,
        source_page_name: SourcePage.my_subscription,
        referrer,
      });
    },
    [logEvent, referrer]
  );

  const baseSubscriptionState = useMemo(
    () => getUISubscriptionState(userSubscription as SubscriptionType),
    [userSubscription]
  );

  const pendingChange = getPendingSubscriptionChange(userSubscription as SubscriptionType);
  // subscription_tier_id is authoritative; getTierForPlanCode is only a fallback (exact match)
  // for payloads missing it.
  const cloudHasPendingDowngrade =
    !!pendingChange?.id &&
    (pendingChange.subscription_tier_id === SubscriptionTier.Core ||
      getTierForPlanCode(pendingChange.plan_code) === SubscriptionTier.Core);
  const cloudPendingDowngradeDate = cloudHasPendingDowngrade
    ? userSubscription?.next_renewal_at ?? undefined
    : undefined;
  const pendingTier: Plan | null = useMemo(() => {
    if (pendingChange?.subscription_tier_id === SubscriptionTier.Pro) return "pro";
    if (pendingChange?.subscription_tier_id === SubscriptionTier.Core) return "core";
    return null;
  }, [pendingChange?.subscription_tier_id]);

  // IAP pending changes carry no real cloud amount, so the store offering prices them.
  // They're always deferred downgrades (upgrades apply immediately), hence the Core default.
  const pendingIapPriceString =
    pendingChange?.source === "iap"
      ? (pendingTier === "pro" ? proPkg : corePkg)?.product.priceString
      : undefined;

  const pendingChangeAmountCents =
    pendingChange?.total_amount_cents ?? pendingChange?.subscription_amount_cents;
  const cloudPendingDowngradePriceString = !cloudHasPendingDowngrade
    ? undefined
    : pendingChange?.source === "iap"
      ? pendingIapPriceString
      : pendingChangeAmountCents !== undefined && pendingChange?.currency
        ? i18n.number(pendingChangeAmountCents / 100, {
            style: "currency",
            currency: pendingChange.currency,
          })
        : undefined;

  // RC's on-device CustomerInfo can't reliably see a scheduled downgrade before it transacts,
  // so cloud (subscription_pending_change) is the sole source here.
  const effectivePendingDowngrade = useMemo(() => {
    if (cloudHasPendingDowngrade) {
      return { date: cloudPendingDowngradeDate, priceString: cloudPendingDowngradePriceString };
    }
    return null;
  }, [cloudHasPendingDowngrade, cloudPendingDowngradeDate, cloudPendingDowngradePriceString]);

  const userSubscriptionState = useMemo(() => {
    if (
      effectivePendingDowngrade &&
      baseSubscriptionState === UISubscriptionStatusEnum.ActiveSubscription
    ) {
      return UISubscriptionStatusEnum.ActiveSubscriptionWithPendingDowngrade;
    }
    return baseSubscriptionState;
  }, [effectivePendingDowngrade, baseSubscriptionState]);

  const hasActiveSub = useMemo(
    () => activeSubscriptionStates.includes(userSubscriptionState),
    [userSubscriptionState]
  );

  const hasTrialNoPendingSub = useMemo(
    () => userSubscriptionState === UISubscriptionStatusEnum.OnTrialNoSubscription,
    [userSubscriptionState]
  );

  const activeTier: Plan = useMemo(() => {
    // Inactive subs grant no tier, even though subscription_tier_id lingers on the record.
    if (!hasActiveSub) return "base";
    if (userSubscription?.subscription_tier_id === SubscriptionTier.Pro) return "pro";
    if (userSubscription?.subscription_tier_id === SubscriptionTier.Core) return "core";
    return "base";
  }, [hasActiveSub, userSubscription?.subscription_tier_id]);

  const redirectToStoreManagement = useCallback(
    (
      processorName: string | undefined,
      processorUrl: string | null | undefined,
      activeTierText: string,
      interval: string
    ): boolean => {
      if (processorName === PROCESSOR.APPLE) {
        setDrawer(
          buildAppleManagementDrawer({
            url: processorUrl ?? APPLE_MANAGE_URL,
            subscriptionLabel: t`${activeTierText} ${interval} via Apple`,
            activeTierText,
          })
        );
        return true;
      }
      if (processorName === PROCESSOR.GOOGLE) {
        setDrawer(
          buildGoogleManagementDrawer({
            url: processorUrl ?? GOOGLE_MANAGE_URL,
            subscriptionLabel: t`${activeTierText} ${interval} via Google Play`,
            activeTierText,
          })
        );
        return true;
      }
      return false;
    },
    [setDrawer]
  );

  const redirectToSystmManagement = useCallback(
    (
      processorName: string | undefined,
      processorUrl: string | null | undefined,
      activeTierText: string,
      interval: string
    ): void => {
      const fallbackUrl = processorName === PROCESSOR.GOOGLE ? GOOGLE_MANAGE_URL : APPLE_MANAGE_URL;
      setDrawer(
        buildSystmManagementDrawer({
          url: processorUrl ?? fallbackUrl,
          subscriptionLabel: t`${activeTierText} ${interval} via SYSTM`,
          activeTierText,
        })
      );
    },
    [setDrawer]
  );

  const handleManageCloudSubscription = useCallback(async () => {
    const processorName = userSubscription?.subscription_processor?.name;
    const processorUrl = userSubscription?.subscription_processor?.url;
    const activeTierText = pendingTier ? capitalize(pendingTier) : capitalize(activeTier);
    const interval = pendingChange?.interval ?? userSubscription?.interval ?? "";

    if (isSystmSubscription(userSubscription) && isWahooApp(Number(fitnessAppId))) {
      redirectToSystmManagement(processorName, processorUrl, activeTierText, interval);
      return;
    }

    const response = await getAuthTokens();
    const { token } = response;
    const url = buildChangePlanUrl(wahooMyAccountUrl, fitnessAppId, token);
    setDrawer(
      buildWahooManagementDrawer({
        url,
        subscriptionLabel: t`${activeTierText} ${interval} via Wahoofitness.com`,
        activeTierText,
        trialing: hasTrialNoPendingSub,
      })
    );
  }, [
    userSubscription,
    fitnessAppId,
    redirectToSystmManagement,
    getAuthTokens,
    wahooMyAccountUrl,
    pendingTier,
    activeTier,
    pendingChange?.interval,
    hasTrialNoPendingSub,
    setDrawer,
  ]);

  const handleMismatchedChangePlan = useCallback(() => {
    const processorName = userSubscription?.subscription_processor?.name;
    const processorUrl = userSubscription?.subscription_processor?.url;
    const interval = userSubscription?.interval ?? "";
    const activeTierText = capitalize(activeTier);
    if (isSystmSubscription(userSubscription)) {
      redirectToSystmManagement(processorName, processorUrl, activeTierText, interval);
      return;
    }
    redirectToStoreManagement(processorName, processorUrl, activeTierText, interval);
  }, [userSubscription, activeTier, redirectToStoreManagement, redirectToSystmManagement]);

  const confirmCancelSubscription = useCallback(
    (tierLabel: string) => {
      const accessUntilLabel =
        userSubscription?.next_renewal_at && isValid(parseISO(userSubscription.next_renewal_at))
          ? formatDate({ date: userSubscription.next_renewal_at, format: "PP" })
          : undefined;
      setDrawer(
        buildCancelConfirmationDrawer({
          tierLabel,
          downgradeTierLabel: capitalize("base"),
          accessUntilLabel,
          onKeepPlan: () => {
            logSubscriptionEvent("cancel_subscription_dismissed", ClickTarget.keep_subscription);
            handleClose();
          },
          onDismiss: () => {
            logSubscriptionEvent("cancel_subscription_dismissed", ClickTarget.close);
          },
          onCancelPlan: () =>
            guardedCancelUpdate(
              async () => {
                logSubscriptionEvent(
                  "cancel_subscription_confirmed",
                  ClickTarget.cancel_subscription
                );
                // Read the processor fresh: the subscription may have been revalidated while
                // this drawer sat open, and we must route to wherever it's *currently* managed.
                const latestSubscription = userSubscriptionRef.current;
                const currentProcessorName = latestSubscription?.subscription_processor?.name;
                const currentProcessorUrl = latestSubscription?.subscription_processor?.url;
                const currentInterval = latestSubscription?.interval ?? "";

                if (
                  isMobileProcessor(currentProcessorName) &&
                  isSystmSubscription(latestSubscription)
                ) {
                  redirectToSystmManagement(
                    currentProcessorName,
                    currentProcessorUrl,
                    tierLabel,
                    currentInterval
                  );
                  return;
                }
                const isMismatch = isAppStoreMismatch(Number(fitnessAppId), currentProcessorName);
                if (
                  isMismatch &&
                  redirectToStoreManagement(
                    currentProcessorName,
                    currentProcessorUrl,
                    tierLabel,
                    currentInterval
                  )
                ) {
                  return;
                }

                if (currentProcessorName === PROCESSOR.APPLE) {
                  handleClose();
                  openExternalLink(currentProcessorUrl ?? APPLE_MANAGE_URL);
                } else if (currentProcessorName === PROCESSOR.GOOGLE) {
                  handleClose();
                  openExternalLink(currentProcessorUrl ?? GOOGLE_MANAGE_URL);
                } else {
                  await handleManageCloudSubscription();
                }
              },
              { errorMessage: t`Failed to cancel your subscription. Please try again.` }
            ),
        })
      );
    },
    [
      userSubscription?.next_renewal_at,
      setDrawer,
      handleClose,
      logSubscriptionEvent,
      handleManageCloudSubscription,
      guardedCancelUpdate,
      fitnessAppId,
      redirectToStoreManagement,
      redirectToSystmManagement,
    ]
  );

  const handleButtonClick = useCallback(
    async (buttonName: ClickTarget) => {
      logSubscriptionEvent("manage_or_cancel_subscription", buttonName);
      const processorName = userSubscription?.subscription_processor?.name;
      const processorUrl = userSubscription?.subscription_processor?.url;
      const interval = userSubscription?.interval ?? "";
      const activeTierText = capitalize(activeTier);

      if (buttonName === ClickTarget.resubscribe) {
        if (!isMobileProcessor(processorName)) {
          handleManageCloudSubscription();
          return;
        }
        if (isSystmSubscription(userSubscription)) {
          redirectToSystmManagement(processorName, processorUrl, activeTierText, interval);
          return;
        }
        const isMismatch = isAppStoreMismatch(Number(fitnessAppId), processorName);
        if (!isMismatch) {
          const pkg =
            userSubscription?.subscription_tier_id === SubscriptionTier.Pro ? proPkg : corePkg;
          makePurchase(pkg?.identifier || "");
          return;
        }
        if (redirectToStoreManagement(processorName, processorUrl, activeTierText, interval)) {
          return;
        }
      }

      // Cancel always shows the Keep-plan/Cancel-plan confirmation first, regardless of
      // SYSTM/app-store-mismatch state — confirmCancelSubscription's onCancelPlan re-derives
      // the correct destination (SYSTM/mismatch/store/cloud) once the user actually confirms.
      if (buttonName === ClickTarget.cancel_subscription) {
        confirmCancelSubscription(activeTierText);
        return;
      }

      if (buttonName === ClickTarget.manage_subscription) {
        if (isMobileProcessor(processorName) && isSystmSubscription(userSubscription)) {
          redirectToSystmManagement(processorName, processorUrl, activeTierText, interval);
          return;
        }
        const isMismatch = isAppStoreMismatch(Number(fitnessAppId), processorName);
        if (
          isMismatch &&
          redirectToStoreManagement(processorName, processorUrl, activeTierText, interval)
        ) {
          return;
        }
      }

      if (processorName === PROCESSOR.APPLE) {
        openExternalLink(processorUrl ?? APPLE_MANAGE_URL);
      } else if (processorName === PROCESSOR.GOOGLE) {
        openExternalLink(processorUrl ?? GOOGLE_MANAGE_URL);
      } else {
        handleManageCloudSubscription();
      }
    },
    [
      logSubscriptionEvent,
      userSubscription,
      activeTier,
      fitnessAppId,
      proPkg,
      corePkg,
      makePurchase,
      handleManageCloudSubscription,
      redirectToStoreManagement,
      redirectToSystmManagement,
      confirmCancelSubscription,
    ]
  );

  const subscriptionData = useMemo((): SubscriptionDisplayData | null => {
    if (!userSubscription) return null;

    const formattedAmount =
      hasActiveSub && userSubscription.subscription_amount_cents && userSubscription.currency
        ? `${i18n.number(userSubscription.subscription_amount_cents / 100, {
            style: "currency",
            currency: userSubscription.currency,
          })}${formatInterval(userSubscription.interval)}`
        : i18n.number(0, { style: "currency", currency: userSubscription.currency || "USD" });

    const processorName = userSubscription.subscription_processor?.name;
    let onChangePlanPrompt: (() => void) | undefined;
    if (!hasActiveSub) {
      // No active subscription to manage — always let the user pick a fresh plan, regardless
      // of what processor value lingers on the record.
      onChangePlanPrompt = undefined;
    } else if (!isMobileProcessor(processorName)) {
      onChangePlanPrompt = handleManageCloudSubscription;
    } else if (
      isAppStoreMismatch(Number(fitnessAppId), processorName) ||
      (isSystmSubscription(userSubscription) && isWahooApp(Number(fitnessAppId)))
    ) {
      onChangePlanPrompt = handleMismatchedChangePlan;
    } else {
      onChangePlanPrompt = undefined;
    }
    if (onChangePlanPrompt) {
      const openChangePlanPrompt = onChangePlanPrompt;
      onChangePlanPrompt = () => {
        logSubscriptionEvent("subscription_change_plan", ClickTarget.change_plan);
        openChangePlanPrompt();
      };
    }

    const ctx: BuildContext = {
      sub: userSubscription as SubscriptionType,
      formattedAmount,
      i18n,
      fitnessAppId: Number(fitnessAppId),
      onButtonClick: handleButtonClick,
      onChangePlanPrompt,
      onSubscribeToPro: () => {
        logSubscriptionEvent("subscription_subscribe_to_pro", ClickTarget.activate_subscription);
        handleManageCloudSubscription();
      },
      activeTier,
      purchaseInProgress,
      pendingDowngradeDate: effectivePendingDowngrade?.date,
      pendingDowngradePriceString: effectivePendingDowngrade?.priceString,
      pendingIapPriceString,
    };

    // If the user doesn't have billing history, there won't be subscription info in the cloud,
    // so there won't be an id.
    if (!hasActiveSub && userSubscription?.id) {
      return buildBaseSubscriptionData(ctx);
    }

    switch (userSubscriptionState) {
      case UISubscriptionStatusEnum.ImmediateIAPSubscription:
        return buildImmediateIAPData(ctx);
      case UISubscriptionStatusEnum.PriceCoupon:
        return buildPriceCouponData(ctx);
      case UISubscriptionStatusEnum.HonorarySubscription:
        return buildHonorarySubscriptionData(ctx);
      case UISubscriptionStatusEnum.OnTrialNoSubscription:
        return buildOnTrialNoSubscriptionData(ctx);
      case UISubscriptionStatusEnum.PaymentError:
        return buildPaymentErrorData(ctx);
      case UISubscriptionStatusEnum.ActiveCancelledSubscription:
        return buildActiveCancelledSubscriptionData(ctx);
      case UISubscriptionStatusEnum.OnTrialFutureSubscription:
        return buildOnTrialFutureSubscriptionData(ctx);
      case UISubscriptionStatusEnum.OnIapTrial:
        return buildOnIapTrialData(ctx);
      case UISubscriptionStatusEnum.ActiveSubscription:
        return buildActiveSubscriptionData(ctx);
      case UISubscriptionStatusEnum.ActiveSubscriptionWithPendingDowngrade:
        return buildActiveSubscriptionWithPendingDowngradeData(ctx);
      case UISubscriptionStatusEnum.HonoraryRecurringSubscription:
        return buildHonoraryRecurringSubscriptionData(ctx);
      case UISubscriptionStatusEnum.ActiveTimeOffPromotion:
        return buildActiveTimeOffPromotionData(ctx);
      case UISubscriptionStatusEnum.TrialingTimeOffPromotion:
        return buildTrialingTimeOffPromotionData(ctx);
      default:
        return null;
    }
  }, [
    userSubscription,
    hasActiveSub,
    i18n,
    fitnessAppId,
    handleButtonClick,
    handleManageCloudSubscription,
    handleMismatchedChangePlan,
    logSubscriptionEvent,
    activeTier,
    purchaseInProgress,
    userSubscriptionState,
    effectivePendingDowngrade,
    pendingIapPriceString,
  ]);

  return {
    userSubscription,
    userSubscriptionState,
    hasActiveSub,
    hasTrialNoPendingSub,
    subscriptionError,
    subscriptionIsLoading,
    subscriptionData,
    offeringsLoading,
    offeringsError,
    makePurchase,
    purchaseInProgress,
    activeTier,
    proPkg,
    corePkg,
    customerInfo,
    convertPeriodToDays,
  };
}

export default useTieredMembership;
